// src/Header.jsx
import React, { useState, useEffect } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Box, 
  IconButton, 
  Typography, 
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import useMicrosoftProfile from './useMicrosoftProfile';
import { supabase } from './supabaseClient';
import { clearCachedPhoto } from './microsoftPhoto';
import RealTimeNotifications from './RealTimeNotifications';

import { brand, surface } from './designTokens';
export default function Header({ pageTitle = 'Dashboard Overview', pageSubtitle = "Welcome back, Admin. Here's what's happening today." }) {
  const { instance } = useMsal();
  const navigate = useNavigate();
  const { profile } = useMicrosoftProfile();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Get user info from Microsoft profile
  const firstName = profile?.firstName || '';
  const lastName = profile?.lastName || '';
  const fullName = profile?.displayName || `${firstName} ${lastName}`.trim() || 'Admin';
  const jobTitle = profile?.jobTitle || '';
  const userInitials = `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || 'A';

  // The role this person actually holds, looked up by the address they signed
  // in with — the same address is_active_admin() matches on, so if this shows
  // nothing they would not have got in here either.
  const [roleLabel, setRoleLabel] = useState('Administrator');
  useEffect(() => {
    const email = profile?.email?.toLowerCase();
    if (!email) return;
    let cancelled = false;
    supabase
      .from('admin_users')
      .select('role')
      .eq('microsoft_email', email)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled || !data?.role) return;
        setRoleLabel(
          data.role
            .split('_')
            .map(w => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' '),
        );
      });
    return () => {
      cancelled = true;
    };
  }, [profile?.email]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSettings = () => {
    handleClose();
    navigate('/dashboard/settings');
  };

  // Sign out of the thing that is actually holding the session.
  //
  // This used to call only instance.logoutPopup(), which signs out of Microsoft
  // and leaves the Supabase session untouched — and Supabase is what
  // authenticates this console. So it navigated to "/", RequireAuth found a
  // valid session still sitting in localStorage, and let the person straight
  // back in. Signing out did not sign anyone out, and on a shared computer the
  // next person arrived already signed in as the last.
  //
  // Order matters: the Supabase sign-out happens first and is awaited, so that
  // a failure in the Microsoft half cannot leave the real session alive.
  const handleLogout = async () => {
    handleClose();

    const userId = (await supabase.auth.getSession())?.data?.session?.user?.id;

    try {
      const { error } = await supabase.auth.signOut();
      if (error) console.error('Supabase sign-out failed:', error);
    } catch (err) {
      console.error('Supabase sign-out threw:', err);
    }

    // Their face should not stay behind for whoever signs in next.
    clearCachedPhoto(userId);

    // Only if MSAL actually has an account. Sign-in goes through Supabase, so
    // usually it does not, and calling this unconditionally throws for nothing.
    try {
      if (instance.getAllAccounts().length > 0) {
        await instance.logoutPopup({ postLogoutRedirectUri: window.location.origin });
      }
    } catch (err) {
      console.error('Microsoft sign-out failed:', err);
    }

    navigate('/', { replace: true });
  };

  return (
    <AppBar
      position="sticky"
      sx={{
        backgroundColor: surface.sunken,
        boxShadow: 'none',
        borderBottom: `1px solid ${surface.line}`,
      }}
    >
      <Toolbar sx={{ minHeight: 64, px: 5, justifyContent: 'space-between', gap: 2 }}>
        {/* Centered title/subtitle */}
        <Box sx={{ flex: 1, minWidth: 0, textAlign: 'center', pr: 2 }}>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 700, 
              color: '#333', 
              mb: 0.5,
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              whiteSpace: 'normal',
            }}
          >
            {pageTitle}
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: '#666',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              whiteSpace: 'normal',
              lineHeight: 1.4,
            }}
          >
            {pageSubtitle}
          </Typography>
        </Box>

        {/* Right side - Search, Notifications, User */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <IconButton sx={{ color: '#666', '&:hover': { color: '#333' } }}>
            <SearchIcon />
          </IconButton>
          
          <RealTimeNotifications />

          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1.5,
              cursor: 'pointer',
              borderRadius: 2,
              px: 1,
              py: 0.5,
              '&:hover': {
                backgroundColor: surface.line,
              },
            }}
            onClick={handleClick}
          >
            {/*
              Their own Microsoft picture when they have one. `src` falling
              back to undefined rather than null matters: MUI only renders the
              children as a fallback when src is absent, and null would give a
              broken image instead of the initials.
            */}
            <Avatar
              src={profile?.photoUrl || undefined}
              alt={fullName}
              sx={{
                width: 40,
                height: 40,
                bgcolor: brand.gold,
                fontSize: '0.875rem',
              }}
            >
              {userInitials}
            </Avatar>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#333', lineHeight: 1.2 }}>
                {firstName && lastName ? `${firstName} ${lastName}` : fullName}
              </Typography>
              {jobTitle && (
                <Typography variant="caption" sx={{ color: '#666', fontSize: '0.7rem', display: 'block', lineHeight: 1.2 }}>
                  {jobTitle}
                </Typography>
              )}
              {!jobTitle && (
                <Typography variant="caption" sx={{ color: '#666', fontSize: '0.7rem', display: 'block', lineHeight: 1.2 }}>
                  {/*
                    Their actual role from admin_users, not the word
                    "Administrator" for everyone. Samantha is a Super Admin and
                    this line told her otherwise.
                  */}
                  {roleLabel}
                </Typography>
              )}
            </Box>
          </Box>

          {/* User Menu */}
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            onClick={handleClose}
            PaperProps={{
              elevation: 3,
              sx: {
                mt: 1.5,
                minWidth: 200,
                borderRadius: 2,
                '& .MuiMenuItem-root': {
                  px: 2,
                  py: 1.5,
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem onClick={handleSettings}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              <Typography variant="body2">Settings</Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <Typography variant="body2">Logout</Typography>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
}
